import { ApiError, CertificateService } from '@/@types/generated'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { buildPath } from '@/routes'
import { XCircleIcon } from 'lucide-react'
import { PropsWithChildren, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useCompany } from '../../hooks/company-provider'
import Loading from '@/components/Atoms/Loading'
import { useError } from '@/hooks/useError'

export function RenewalInFlightGuard({ children }: PropsWithChildren) {
  const { showError } = useError()
  const navigate = useNavigate()
  const { company } = useCompany()
  const [isInFlight, setIsInFlight] = useState<boolean | null>(null)
  // Set when renewal is blocked for another reason (e.g. an unpaid vehicle registration fee)
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)

  const certificateNumber = company.motorCarrier?.certificate?.certificateNumber

  // Check for any pending payments
  useEffect(() => {
    if (!certificateNumber) return

    CertificateService.getCertificateRenewalEligibility(certificateNumber)
      .then((res) => {
        // 204 (no body) = eligible; 200 with a message = blocked
        setBlockedMessage(res?.message ?? null)
        setIsInFlight(false)
      })
      .catch((err) => {
        // 409 Conflict represents ineligible renewal
        if (err instanceof ApiError && err.status === 409) {
          setIsInFlight(true)
          return
        }

        showError(err, 'Failed to verify pending payments')
        navigate(
          buildPath('/companies/:businessEntityId', {
            businessEntityId: company.id,
          })
        )
      })
  }, [certificateNumber, company.id, navigate, showError])

  // Loading
  if (isInFlight === null)
    return (
      <Loading
        variant="fullscreen"
        message="Verifying renewal eligibility..."
      />
    )

  // Ensure carrier has no in-flight payment
  if (isInFlight === true)
    return (
      <div className="mt-6 grid justify-center gap-4 text-center">
        <XCircleIcon
          className="stroke-destructive h-24 w-24 justify-self-center"
          aria-hidden="true"
        />
        <Header variant="h3">Pending Renewal Payment</Header>
        <p>This certificate already has a renewal payment in progress.</p>
        <p>
          Please wait for that payment to complete before starting a new
          renewal. Some payments can take up to 20 minutes to process.
        </p>

        <div className="flex justify-center gap-4">
          <Button asChild>
            <Link
              to={buildPath('/companies/:businessEntityId/payments', {
                businessEntityId: company.id,
              })}
            >
              View Pending Payments
            </Link>
          </Button>
        </div>
      </div>
    )

  // Renewal blocked until something else is resolved first
  if (blockedMessage)
    return (
      <div className="mt-6 grid justify-center gap-4 text-center">
        <XCircleIcon
          className="stroke-destructive h-24 w-24 justify-self-center"
          aria-hidden="true"
        />
        <Header variant="h3">Unpaid Vehicle Registration Fee</Header>
        <p>{blockedMessage}</p>

        <div className="flex justify-center gap-4">
          <Button asChild>
            <Link
              to={buildPath('/companies/:businessEntityId/payments', {
                businessEntityId: company.id,
              })}
            >
              View Payments
            </Link>
          </Button>
        </div>
      </div>
    )

  return children
}
